import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Card from '../../components/common/Card';
import PasswordInput from '../../components/forms/PasswordInput';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { passwordResetService } from '../../services/passwordReset.service';
import { useForm } from '../../hooks/useForm';
import { validators } from '../../utils/validators';
import { theme } from '../../config/theme';
import { XCircleIcon } from '../../components/icons/Icons';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const { values, errors, touched, handleChange, handleBlur, validate } = useForm(
    {
      new_password: '',
      confirm_password: '',
    },
    {
      new_password: (value) => validators.password(value),
      confirm_password: (value, all) => validators.confirmPassword(value, all.new_password),
    }
  );

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await passwordResetService.validateToken(token);
      if (response.data.valid) {
        setTokenValid(true);
        setUserEmail(response.data.email);
      } else {
        setTokenValid(false);
      }
    } catch (error) {
      setTokenValid(false);
      toast.error('Invalid or expired reset link');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await passwordResetService.resetPassword(token, values.new_password, values.confirm_password);
      toast.success('Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return <LoadingSpinner fullScreen />;
  }

  if (!tokenValid) {
    return (
      <div style={{ width: '100%', maxWidth: '450px' }}>
        <div style={{ textAlign: 'center', marginBottom: theme.spacing['2xl'] }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <h1 style={{ fontSize: theme.typography.fontSize['4xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary.main, marginBottom: theme.spacing.md }}>
              AlarcTrade
            </h1>
          </Link>
        </div>

        <Card padding="xl">
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginBottom: theme.spacing.lg, 
              color: theme.colors.error 
            }}>
              <XCircleIcon size={48} />
            </div>
            <h2 style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.md }}>
              Invalid or Expired Link
            </h2>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginBottom: theme.spacing.xl }}>
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link to="/forgot-password">
              <Button>Request New Link</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '450px' }}>
      <div style={{ textAlign: 'center', marginBottom: theme.spacing['2xl'] }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <h1 style={{ fontSize: theme.typography.fontSize['4xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary.main, marginBottom: theme.spacing.md }}>
            AlarcTrade
          </h1>
        </Link>
        <p style={{ fontSize: theme.typography.fontSize.base, color: theme.colors.text.secondary }}>
          Create a new password
        </p>
      </div>

      <Card padding="xl">
        <h2 style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, textAlign: 'center', marginBottom: theme.spacing.md }}>
          Reset Password
        </h2>
        <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, textAlign: 'center', marginBottom: theme.spacing.xl }}>
          Resetting password for: <strong>{userEmail}</strong>
        </p>

        <form onSubmit={handleSubmit}>
          <PasswordInput
            label="New Password"
            name="new_password"
            value={values.new_password}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter new password"
            error={errors.new_password}
            touched={touched.new_password}
            required
          />

          <PasswordInput
            label="Confirm New Password"
            name="confirm_password"
            value={values.confirm_password}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Confirm new password"
            error={errors.confirm_password}
            touched={touched.confirm_password}
            required
          />

          <Button type="submit" fullWidth loading={loading} disabled={loading}>
            Reset Password
          </Button>
        </form>

        <div style={{ marginTop: theme.spacing.xl, textAlign: 'center', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
          Remember your password?{' '}
          <Link to="/login" style={{ color: theme.colors.primary.main, textDecoration: 'none', fontWeight: theme.typography.fontWeight.medium }}>
            Sign In
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;